/**
 * Cliente HTTP da tela.
 *
 * Um lugar só para três coisas que, espalhadas, viram bug: sessão vencida,
 * modo somente-leitura e o token de primeira execução.
 */

/** Erro que a tela sabe mostrar. */
export class ErroApi extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly corpo: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ErroApi';
  }

  /** Sessão vencida ou inexistente: a tela inteira volta para o login. */
  get precisaLogin(): boolean {
    return this.status === 401 && this.corpo['precisaLogin'] === true;
  }

  /** 423: o produto está em somente-leitura. Não é falta de permissão. */
  get somenteLeitura(): boolean {
    return this.status === 423;
  }
}

/**
 * Token de primeira execução.
 *
 * Guardado em `sessionStorage`, não em `localStorage`: ele vale para uma
 * instalação em andamento, não para sempre. Fechar a aba encerra o assistente —
 * que é o comportamento certo para uma credencial impressa em log.
 */
const CHAVE_TOKEN = 'lenanzo.instalacao';

export const guardarTokenInstalacao = (t: string): void =>
  sessionStorage.setItem(CHAVE_TOKEN, t);
export const lerTokenInstalacao = (): string | null =>
  sessionStorage.getItem(CHAVE_TOKEN);
export const esquecerTokenInstalacao = (): void => sessionStorage.removeItem(CHAVE_TOKEN);

/**
 * O `correlationId` da última resposta do servidor.
 *
 * 🔴 **É o que a pessoa cita ao abrir chamado.** Sem ele, o suporte começa a
 * investigação perguntando "que horas foi mais ou menos?" — e num parque de 7
 * mil máquinas, uma faixa de tempo não estreita nada. Com ele, é uma busca.
 *
 * Guardado num módulo em vez de estado de React de propósito: o valor vem do
 * cabeçalho de QUALQUER requisição, inclusive das que falharam, e componente
 * nenhum precisa saber disso para exibi-lo.
 */
let ultimaCorrelacao: string | null = null;
export const correlacaoAtual = (): string | null => ultimaCorrelacao;

async function pedir<T>(
  metodo: string,
  rota: string,
  corpo?: unknown,
): Promise<T> {
  const token = lerTokenInstalacao();
  const cabecalhos: Record<string, string> = {};
  if (corpo !== undefined) cabecalhos['Content-Type'] = 'application/json';
  if (token !== null) cabecalhos['x-lenanzo-instalacao'] = token;

  const r = await fetch(rota, {
    method: metodo,
    headers: cabecalhos,
    ...(corpo === undefined ? {} : { body: JSON.stringify(corpo) }),
  });

  // Vale para resposta de erro também — é justamente a que alguém vai reportar.
  ultimaCorrelacao = r.headers.get('x-correlation-id') ?? ultimaCorrelacao;

  const texto = await r.text();
  let json: Record<string, unknown> = {};
  try {
    json = texto === '' ? {} : (JSON.parse(texto) as Record<string, unknown>);
  } catch {
    // Resposta que não é JSON só acontece quando algo muito errado aconteceu
    // (proxy no meio, HTML de erro). Mostrar o começo do texto ajuda mais que
    // "resposta inválida".
    if (!r.ok) throw new ErroApi(texto.slice(0, 200) || `HTTP ${r.status}`, r.status);
  }

  if (!r.ok) {
    const msg =
      (typeof json['erro'] === 'string' ? json['erro'] : null) ??
      listaDeErros(json['erros']) ??
      (typeof json['detalhe'] === 'string' ? json['detalhe'] : null) ??
      `HTTP ${r.status}`;
    throw new ErroApi(msg, r.status, json);
  }
  return json as T;
}

/**
 * Erro de validação vem em lista, não em frase.
 *
 * 🔴 **Toda mensagem de validação do produto estava sendo jogada fora.** O
 * servidor responde `{ erros: [{ campo, mensagem }] }` em oito rotas —
 * configuração, integrações, painéis e regras — e o cliente só procurava
 * `erro`, no singular. A explicação existia, chegava ao navegador e morria
 * aqui; a pessoa via **"HTTP 400"**.
 *
 * Medido ao editar um cartão: a resposta dizia, com todas as letras, que uma
 * consulta de agrupamento não vira cartão de número e sugeria "barras". Nada
 * disso apareceu na tela.
 */
function listaDeErros(v: unknown): string | null {
  if (!Array.isArray(v) || v.length === 0) return null;
  const frases = v
    .map((e: unknown) => {
      if (typeof e === 'string') return e;
      const o = (e ?? {}) as Record<string, unknown>;
      const m = o['mensagem'] ?? o['message'] ?? o['erro'];
      return typeof m === 'string' ? m : null;
    })
    .filter((x): x is string => x !== null && x !== '');

  return frases.length === 0 ? null : frases.join(' ');
}

export const api = {
  get: <T,>(rota: string): Promise<T> => pedir<T>('GET', rota),
  post: <T,>(rota: string, corpo?: unknown): Promise<T> => pedir<T>('POST', rota, corpo),
  put: <T,>(rota: string, corpo?: unknown): Promise<T> => pedir<T>('PUT', rota, corpo),
  del: <T,>(rota: string): Promise<T> => pedir<T>('DELETE', rota),
};

/** Query string sem os campos vazios — `?estado=` já causou 400 neste produto. */
export function qs(p: Record<string, string | number | boolean | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === '' || v === false) continue;
    u.set(k, String(v));
  }
  const s = u.toString();
  return s === '' ? '' : `?${s}`;
}
